import { Button } from "@/components/ui/button"
import { FormField } from "pages/GenericOrder/ui-form/form-field"
import { ModalForm } from "pages/GenericOrder/ui-form/modal-form"
import { useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { z } from "zod"
import { updateServiceCharges } from "../apis"
import { toast } from "sonner"
import { useParams } from "react-router-dom"
import { CurrencyInput } from "components/Input/Inputs"

const schema = z.object({
    quantity: z
        .number({
            invalid_type_error: 'Quantity is required',
            required_error: 'Quantity is required',
        })
        .min(0, 'Quantity must be at least 0')
        .refine(val => !isNaN(val), {
            message: 'Quantity must be a number',
        }),
})

type FieldValues = z.infer<typeof schema>

export default function EditServiceCharge({ children, quantity, currency }: { children: React.ReactNode, quantity: number, currency: string }) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { id } = useParams()
    const queryClient = useQueryClient()
    const { mutate, isLoading } = useMutation({
        mutationFn: updateServiceCharges,
        onSuccess: () => {
            queryClient.invalidateQueries(['orderDetails'])
            toast.success('Product updated successfully')
            setIsOpen(false)
        },
        onError: () => {
            toast.error('Failed to update product')
        }
    })
    const handleSubmit = (data: FieldValues) => {
        mutate({
            order_id: Number(id!),
            amount: data?.quantity
        })
    }
    const defaultValues = {
        quantity,
    }
    return (
        <>
            <Button variant='ghost' className="p-0 h-min" onClick={() => setIsOpen(true)}>
                {children}
            </Button>
            <ModalForm<FieldValues>
                title="Edit Service Charge"
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onSubmit={data => handleSubmit(data)}
                submitButtonText="Save"
                schema={schema}
                defaultValues={defaultValues}
                isLoading={isLoading}
                skipDirtyCheck={true}
            >
                {
                    (form) => {
                        return (
                            <div className="py-4 space-y-2">
                                <FormField
                                    form={form}
                                    name='quantity'
                                    label="Enter Service Charge"
                                    render={({ field }) => (
                                        <CurrencyInput
                                            field={{
                                                ...field,
                                                onChange: e => {
                                                    let value = e.target.value;

                                                    // Handle empty input
                                                    if (value === '') {
                                                        field.onChange(0);
                                                        return;
                                                    }

                                                    // Remove leading zeros, but keep single zero
                                                    if (value.length > 1 && value.startsWith('0')) {
                                                        value = value.replace(/^0+/, '');
                                                        // If we stripped all characters, set to '0'
                                                        if (value === '') {
                                                            value = '0';
                                                        }
                                                    }

                                                    const numericValue = parseInt(value, 10);
                                                    field.onChange(isNaN(numericValue) ? 0 : Math.max(0, numericValue));
                                                }
                                            }}
                                            currencyType={currency}
                                        />
                                    )}
                                />
                            </div>
                        )
                    }
                }
            </ModalForm>
        </>
    )
}